import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Generate a persistent visitor ID
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Generate a session ID (expires after 30 minutes of inactivity)
const getSessionId = (): string => {
  const sessionKey = 'session_id';
  const sessionTimeKey = 'session_time';
  const now = Date.now();
  const lastActivity = localStorage.getItem(sessionTimeKey);
  
  // Check if session expired (30 minutes)
  if (lastActivity && (now - parseInt(lastActivity)) < 30 * 60 * 1000) {
    localStorage.setItem(sessionTimeKey, now.toString());
    return localStorage.getItem(sessionKey) || createNewSession();
  }
  
  return createNewSession();
};

const createNewSession = (): string => {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('session_id', sessionId);
  localStorage.setItem('session_time', Date.now().toString());
  return sessionId;
};

// Get device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Get browser name
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
};

export const useVisitorTracking = () => {
  const { user } = useAuth();

  useEffect(() => {
    const trackVisit = async () => {
      try {
        const visitorId = getVisitorId();
        const sessionId = getSessionId();
        
        // Track the page view via edge function to capture IP and country
        const { data, error } = await supabase.functions.invoke('track-visitor', {
          body: {
            visitorId,
            sessionId,
            pageUrl: window.location.href,
            referrer: document.referrer || null,
            userAgent: navigator.userAgent,
            deviceType: getDeviceType(),
            browser: getBrowser(),
            userId: user?.id || null
          }
        });

        if (error) throw error;
      } catch (error) {
        // Silently fail - analytics should not break the app
        console.error('Visitor tracking error:', error);
      }
    };

    trackVisit();
  }, [user?.id]);
};
